/**
 * Form06_FallAssessmentSurvey.jsx
 * Collection: fall_assessment_surveys
 * Redirect: /forms/07-patient-care-authorization
 */
import React, { useRef, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { FormShell, Field, TriState, saveToMongo, downloadPDF, useToast, C } from "../shared/formKit";
import SignatureCanvas from "react-signature-canvas";

const API_URL = "/api/forms/fall-assessment";
const NEXT    = "/forms/07-patient-care-authorization";

const QUESTIONS = [
  [1,  "Do you ever lose your balance or feel dizzy or unsteady?"],
  [2,  "Have you continued to experience dizziness after an injury or accident?"],
  [3,  "Do you feel unsteady when you are walking or climbing stairs?"],
  [4,  "Do you feel dizzy while sitting down or rising from a seated or lying position?"],
  [5,  "Does walking down the aisle of a supermarket or stopping next to moving traffic make you dizzy?"],
  [6,  "Does moving your head quickly make you dizzy or cause you to feel nauseous?"],
  [7,  "Are you dizzy or unsteady when you first get up in the morning?"],
  [8,  "Do you ever fall or feel like you are about to fall for no apparent reason?"],
  [9,  "Do you use a walker, cane, or any other form of assistance for your mobility?"],
  [10, "Have you had a recent loss of, or decrease in, your vision or hearing?"],
  [11, "Do you fear falling?"],
  [12, "Have you experienced dizziness, vertigo, or serious imbalance in the past six months?"],
  [13, "Has your balance problem caused problems in your social life?"],
  [14, "Have you fallen more than once in the past year without an obvious cause?"],
  [15, "Does dizziness or imbalance interfere with your job or your household responsibilities?"],
];

const initAnswers = () => Object.fromEntries(QUESTIONS.map(([n])=>[`q${n}`, ""]));

export default function FallAssessmentSurvey({ patientName="", patientAge="", patientGender="", onSubmit }) {
  const history = useHistory();
  const formRef  = useRef(null);
  const sigRef   = useRef(null);
  const [sigEmpty, setSigEmpty]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId]       = useState(null);
  const [showToast, Toast]          = useToast();

  const [answers, setAnswers] = useState(initAnswers);
  const [balanceTestingDone, setBalanceTestingDone] = useState("");
  const [sigDate, setSigDate] = useState(new Date().toISOString().split("T")[0]);

  const setA = useCallback((k,v) => setAnswers(a=>({...a,[k]:v})),[]);
  const getSig = () => (!sigRef.current||sigRef.current.isEmpty()) ? null : sigRef.current.toDataURL("image/png");

  // Risk calculation
  const atRisk = Object.values(answers).some(v=>v==="yes"||v==="sometimes");

  const buildDoc = () => ({
    formType: "fall_assessment_survey",
    patient:  { name:patientName, age: patientAge!==""?Number(patientAge):null, gender:patientGender },
    answers,
    atRisk,
    balanceTestingDoneInPast12Months: balanceTestingDone,
    authorization: {
      sigDate:          sigDate||null,
      signatureDataURL: getSig(),
    },
    meta: { submittedAt:new Date().toISOString(), formVersion:"1.0.0" },
  });

  const handleSubmit = async () => {
    if (sigEmpty) { showToast("Please sign before submitting.","error"); return; }
    setSubmitting(true);
    try {
      const doc = buildDoc();
      const res = typeof onSubmit==="function" ? await onSubmit(doc) : await saveToMongo(doc, API_URL);
      if (res?._id) setSavedId(res._id);
      showToast("Saved! Redirecting…","success");
      setTimeout(()=>history(NEXT),1800);  
      //setTimeout(()=>history.push(NEXT),1800);
    } catch(e){ console.error(e); showToast("Save failed.","error"); }
    finally { setSubmitting(false); }
  };

  const handleDownload = async () => {
    try { await downloadPDF(formRef,`fall-assessment-${patientName||"patient"}.pdf`); showToast("Downloaded!"); }
    catch { showToast("PDF failed.","error"); }
  };

  return (
    <FormShell title="Fall Assessment Survey"
      subtitle="To help determine if you may be at risk of a fall or have a balance disorder. Please answer each question honestly."
      dbLabel="MongoDB · fall_assessment_surveys"
      patientName={patientName} patientAge={patientAge} patientGender={patientGender}
      savedId={savedId} formRef={formRef}
      onSubmit={handleSubmit} onDownload={handleDownload} submitting={submitting}>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">ℹ</span>Instructions</div>
        <div className="wf-policy">
          <p>If you answer yes to one or more of the questions, you could be at risk. The best way to determine if you have a problem is to share with the doctor any fears or concerns you have regarding falling, dizziness, or vertigo.</p>
        </div>
      </div>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">Q</span>Balance Self-Test</div>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {QUESTIONS.map(([n,q])=>(
            <div key={n} style={{padding:"14px 16px",border:`1.5px solid ${C.border}`,borderRadius:10,
              background: answers[`q${n}`]==="yes"?"#FEF3C7": answers[`q${n}`]==="sometimes"?C.tealLt:C.white}}>
              <div style={{fontSize:14,fontWeight:600,marginBottom:10,color:C.navy}}>
                {n}. {q}
              </div>
              <TriState name={`q${n}`} value={answers[`q${n}`]} onChange={v=>setA(`q${n}`,v)} />
            </div>
          ))}
        </div>
      </div>

      {atRisk && (
        <div className="wf-section">
          <div className="wf-section-title"><span className="num">16</span>Follow-up Question</div>
          <div className="wf-policy" style={{marginBottom:14}}>
            <p>You answered "Yes / Often" or "Sometimes" to one or more questions, indicating you may be at risk of falling and will need further evaluation.</p>
          </div>
          <Field label="Have you had any balance testing for fall risk done in the past 12 months?">
            <div className="wf-radio-group">
              {["Yes","No"].map(o=>(
                <label className="wf-radio-label" key={o}>
                  <input type="radio" name="balanceTesting" value={o}
                    checked={balanceTestingDone===o} onChange={e=>setBalanceTestingDone(e.target.value)} />{o}
                </label>
              ))}
            </div>
          </Field>
        </div>
      )}

      {atRisk && (
        <div style={{padding:"12px 16px",background:"#FEF3C7",border:"1.5px solid #FCD34D",borderRadius:10,marginBottom:24,fontSize:13,color:"#92400E"}}>
          ⚠️ <strong>Risk Identified:</strong> Based on your responses, you may be at risk of falling. Please share any concerns with your physician during your visit.
        </div>
      )}

      <div className="wf-section" style={{borderBottom:"none"}}>
        <div className="wf-section-title"><span className="num">✍</span>Patient Signature</div>
        <Field label="Date" style={{marginBottom:16,maxWidth:200}}>
          <input type="date" value={sigDate} onChange={e=>setSigDate(e.target.value)} />
        </Field>
        <Field label="Patient Signature (draw below)">
          <div className="wf-sig-box">
            {sigEmpty && <div className="wf-sig-hint">Sign here</div>}
            <SignatureCanvas ref={sigRef} penColor={C.navy}
              canvasProps={{height:150,style:{width:"100%"}}}
              onBegin={()=>setSigEmpty(false)} />
            <div className="wf-sig-actions">
              <button className="btn btn-ghost" type="button"
                onClick={()=>{sigRef.current?.clear();setSigEmpty(true);}}>Clear</button>
            </div>
          </div>
        </Field>
        <p style={{marginTop:12,fontSize:11,color:C.muted,lineHeight:1.5}}>
          Note: Patients are solely responsible for maintaining the privacy and security of all information printed from the Patient Portal.
        </p>
      </div>

      {Toast}
    </FormShell>
  );
}

/*
── Mongoose Schema ────────────────────────────────────────────────────────────
const FallAssessmentSchema = new Schema({
  formType: { type:String, default:"fall_assessment_survey" },
  patient:  { name:String, age:Number, gender:String },
  answers: {
    q1:String,q2:String,q3:String,q4:String,q5:String,
    q6:String,q7:String,q8:String,q9:String,q10:String,
    q11:String,q12:String,q13:String,q14:String,q15:String,
  },
  atRisk: Boolean,
  balanceTestingDoneInPast12Months: String,
  authorization: { sigDate:Date, signatureDataURL:String },
  meta: { submittedAt:Date, formVersion:String },
}, { timestamps:true, collection:"fall_assessment_surveys" });
*/
